/**
 * The optimistic-lock plumbing, in one place — SPEC-2 §7 / SPEC-METHODS §5.3.
 *
 * Two jobs, and both exist so a screen cannot get concurrency wrong by
 * omission:
 *
 *  1. **The version is injected, not remembered.** Call sites write
 *     `update({ summary })` and the hook adds `version` from the entity it was
 *     handed. A screen that forgot it would compile (the field is required at
 *     the resource layer, so it would not) — but more to the point, a screen
 *     that had to remember it on eight call sites would eventually miss one,
 *     and the failure mode is silent: someone's edit disappears.
 *
 *  2. **A 409 becomes state, not an exception.** The conflict is handed back
 *     as data so the view can render `<ConflictSheet>`; every other error stays
 *     an error and reaches the toast unchanged.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { VersionConflictError } from "@/api/client";

export interface VersionedUpdate<TBody> {
  /** Fire the update. Resolves true on success, false when it conflicted. */
  run: (body: TBody) => Promise<boolean>;
  pending: boolean;
  /** Non-null while a conflict is unresolved; drives the sheet. */
  conflict: VersionConflictError | null;
  /** What this client tried to write, so the sheet can show both sides. */
  attempted: Record<string, unknown> | undefined;
  /** "Перечитать" — refetch and drop the conflict. */
  reload: () => void;
  /** "Отменить" — drop the conflict, leave the server's state alone. */
  dismiss: () => void;
}

export function useVersionedUpdate<TBody extends Record<string, unknown>, TResult>({
  version,
  mutate,
  invalidate,
  onSuccess,
  onError,
}: {
  /** The version last read. `null` disables the call entirely. */
  version: number | null | undefined;
  mutate: (body: TBody & { version: number }) => Promise<TResult>;
  /** Query keys to invalidate on success and on "перечитать". */
  invalidate: QueryKey[];
  onSuccess?: (result: TResult) => void;
  onError?: (error: Error) => void;
}): VersionedUpdate<TBody> {
  const qc = useQueryClient();
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);
  const [attempted, setAttempted] = useState<Record<string, unknown> | undefined>();

  const mutation = useMutation({
    mutationFn: (body: TBody) => {
      if (version == null) {
        return Promise.reject(new Error("Запись ещё не загружена"));
      }
      return mutate({ ...body, version } as TBody & { version: number });
    },
    onSuccess: (result) => {
      for (const key of invalidate) qc.invalidateQueries({ queryKey: key });
      onSuccess?.(result);
    },
  });

  const run = useCallback(
    async (body: TBody): Promise<boolean> => {
      try {
        await mutation.mutateAsync(body);
        return true;
      } catch (error) {
        if (error instanceof VersionConflictError) {
          setAttempted(body);
          setConflict(error);
          return false;
        }
        onError?.(error as Error);
        return false;
      }
    },
    [mutation, onError],
  );

  // The key list is held in a ref rather than in the dependency array: it is
  // a fresh array literal on every render, so depending on it would rebuild
  // `reload` constantly, and serialising it to compare would be a dependency
  // the linter (rightly) refuses to reason about.
  const keys = useRef(invalidate);
  useEffect(() => {
    keys.current = invalidate;
  });

  const reload = useCallback(() => {
    for (const key of keys.current) qc.invalidateQueries({ queryKey: key });
    setConflict(null);
    setAttempted(undefined);
  }, [qc]);

  const dismiss = useCallback(() => {
    setConflict(null);
    setAttempted(undefined);
  }, []);

  return { run, pending: mutation.isPending, conflict, attempted, reload, dismiss };
}
